import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Row,
  Col,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  FormGroup,
  Label
} from 'reactstrap';
import { toast } from 'react-toastify';

import DefaultModal from 'components/Modal/DefaultModal';
import Pagination from 'components/Pagination';
import ReactButton from 'components/Buttons/ReactButton';
import {
  icon_column_style,
  individual_column_style,
  individual_header_style,
  individual_icon_header_style
} from '../../../../../assets/styles/table_custom_style';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import BootstrapTable from 'react-bootstrap-table-next';
import ReactLoader from 'components/Loader';
import CustomerPortfolioService from '../../../CustomerPortfolio/service';
import ChangeAccountCustomerPortfolio from '../handleChangeAccountPortfolioId';
import { BiTransfer } from 'react-icons/bi';
import { DEFAULT_PAGE_SIZE } from '../../../utils';

const HandleAccountsModal = ({
  isModalOpen,
  onModalToggle,
  customerPortfolioId,
  ...props
}) => {
  const [activePage, setActivePage] = useState(1);
  const [quantity, setQuantity] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedRows, setSelectedRows] = useState([]);
  const [accounts, setAccounts] = useState(null);
  const [idResource, setIdResource] = useState();
  const [openModalConfirmation, setOpenModalConfirmation] = useState(false);
  const [timeoutID, setTimeoutID] = useState(null);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [isSelectingAll, setIsSelectingAll] = useState(false);

  useEffect(() => {
    if (isModalOpen) {
      clearAllSelected();
    }
  }, [isModalOpen]);

  const handleGetAccountsFromCustomerPortfolio = useCallback(
    (page, size = DEFAULT_PAGE_SIZE, q) => {
      if (isModalOpen) {
        setLoading(true);

        const params = { page, size, q };

        CustomerPortfolioService.getAllAccountsByPortfolioId(
          customerPortfolioId,
          params
        )
          .then((response) => {
            const registers = response.data.data.content;
            const count = response.data.data.quantity;
            setAccounts(registers);
            setQuantity(count);
          })
          .catch(() => {
            toast.error('Erro ao listar contas dessa categoria de cliente');
          })
          .finally(() => {
            setLoading(false);
          });
      }
    },
    [isModalOpen, customerPortfolioId]
  );

  useEffect(() => {
    handleGetAccountsFromCustomerPortfolio(1);
  }, [handleGetAccountsFromCustomerPortfolio]);

  useEffect(() => {
    if (selectedRows.length === quantity && quantity > 0) {
      setIsAllSelected(true);
    } else {
      setIsAllSelected(false);
    }
  }, [quantity, selectedRows.length]);

  const handleOnSelect = (row, isNotSelectedYet) => {
    if (isNotSelectedYet) {
      const newSelected = {
        id: row.id,
        nome: row.name,
        telephone: row.telephone
      };
      setSelectedRows([...selectedRows, newSelected]);
    } else {
      setSelectedRows(selectedRows.filter((x) => x.id !== row.id));
    }
  };

  const handleOnSelectAll = (isSomeSelected, rows) => {
    if (isSomeSelected) {
      const allRows = [...selectedRows, ...rows].map((r) => {
        return { id: r.id, nome: r.name, telephone: r.telephone };
      });
      setSelectedRows(allRows);
    } else {
      const idsToRemove = rows.map((row) => row.id);
      setSelectedRows((currentSelected) => {
        return currentSelected.filter(({ id }) => !idsToRemove.includes(id));
      });
    }
  };

  const handlePageChange = (activePage) => {
    setActivePage(activePage);
    handleGetAccountsFromCustomerPortfolio(activePage);
  };

  const undoSelection = () => {
    setSelectedRows([]);
  };

  const controlChangeCustomerPortfolio = (id) => {
    setIdResource(id);
    setOpenModalConfirmation(true);
  };

  const clearAllSelected = () => {
    setSelectedRows([]);
    setIsAllSelected(false);
    setIdResource(null);
  };

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedRows([]);
      setIsAllSelected(false);
      return;
    }

    setIsSelectingAll(true);

    CustomerPortfolioService.getAllAccountsByPortfolioId(
      customerPortfolioId,
      {}
    )
      .then((response) => {
        const registers = response.data.data.content;
        if (!registers) return;

        const registersSelectionDTO = registers.map((row) => ({
          id: row.id,
          nome: row.name,
          telephone: row.telephone
        }));

        setIsAllSelected(true);
        setSelectedRows(registersSelectionDTO);
      })
      .catch(() => {
        toast.error('Erro ao selecionar todas as contas');
      })
      .finally(() => {
        setIsSelectingAll(false);
      });
  };

  const handleOnSearch = (event) => {
    if (typeof timeoutID === 'number') {
      clearTimeout(timeoutID);
    }
    const q = event.target.value;
    setTimeoutID(
      setTimeout(() => {
        handleGetAccountsFromCustomerPortfolio(1, DEFAULT_PAGE_SIZE, q);
      }, 1000)
    );
  };

  return (
    <>
      <DefaultModal
        show={isModalOpen}
        onModalToggle={onModalToggle}
        title="Gerenciar contas dessa categoria de cliente"
        showCloseButton={false}
        modalContainerClasses="modal-xl"
        {...props}
      >
        <>
          <Row>
            <Col className="d-flex flex-row justify-content-between">
              <div className="d-flex flex-column justify-content-between">
                <span>
                  <strong>
                    {selectedRows.length || '0'}
                    {`/${quantity} `}
                  </strong>
                  {selectedRows.length.length === 1 ? 'conta ' : 'contas '}
                  selecionados
                </span>
                <div className="small">
                  <Button
                    outline={true}
                    size="sm"
                    color=""
                    disabled={selectedRows.length < 1}
                    onClick={() => {
                      if (!selectedRows || !selectedRows.length) return;
                      const ids = selectedRows.map((row) => row.id);
                      controlChangeCustomerPortfolio(ids);
                    }}
                  >
                    <BiTransfer size={24} /> Trocar a categoria dos selecionados
                  </Button>
                </div>
                <div className="small">
                  <Button
                    outline={true}
                    size="sm"
                    color=""
                    disabled={selectedRows.length < 1}
                    onClick={undoSelection}
                  >
                    <li className="fas fa-undo"></li> Desfazer seleção
                  </Button>
                </div>
              </div>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col>
              <InputGroup>
                <InputGroupAddon addonType="prepend">
                  <InputGroupText>
                    <i className="fas fa-search" />
                  </InputGroupText>
                </InputGroupAddon>
                <Input
                  size="md"
                  placeholder="Buscar por nome ou telefone"
                  type="text"
                  defaultValue=""
                  onChange={handleOnSearch}
                />
              </InputGroup>
            </Col>
          </Row>
          <Row className="mt-3 pl-3 small">
            <Col>
              <FormGroup check>
                <Label
                  style={{
                    cursor: isSelectingAll || quantity < 1 ? 'auto' : 'pointer'
                  }}
                  check
                >
                  <Input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    disabled={isSelectingAll || quantity < 1}
                  />
                  <span
                    style={{ userSelect: 'none' }}
                  >{`Selecionar todos (${quantity})`}</span>
                </Label>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col>
              <ToolkitProvider
                data={accounts || []}
                keyField="id"
                loading={loading}
                columns={[
                  {
                    dataField: 'name',
                    text: 'Nome',
                    sort: true,
                    style: individual_column_style,
                    headerStyle: individual_header_style
                  },
                  {
                    dataField: 'telephone',
                    text: 'Telefone',
                    sort: true,
                    style: individual_column_style,
                    headerStyle: individual_header_style
                  },
                  {
                    dataField: 'trocar carteira',
                    style: icon_column_style,
                    headerStyle: individual_icon_header_style,
                    text: 'trocar categoria',
                    formatter: (_, row) => {
                      return (
                        <Button
                          color="info"
                          size="sm"
                          onClick={() =>
                            controlChangeCustomerPortfolio([row.id])
                          }
                        >
                          <BiTransfer size={24} />
                        </Button>
                      );
                    }
                  }
                ]}
              >
                {(props) => (
                  <div className="py-4 table-responsive">
                    {loading ? (
                      <Row className="justify-content-md-center">
                        <ReactLoader />
                      </Row>
                    ) : (
                      <BootstrapTable
                        selectRow={{
                          mode: 'checkbox',
                          selected: selectedRows.map((r) => r.id),
                          onSelect: handleOnSelect,
                          onSelectAll: handleOnSelectAll,
                          selectColumnStyle: individual_header_style
                        }}
                        {...props.baseProps}
                        bootstrap4={true}
                        bordered={false}
                        classes="align-items-center"
                      />
                    )}
                  </div>
                )}
              </ToolkitProvider>
              {!!quantity && quantity > DEFAULT_PAGE_SIZE && (
                <Pagination
                  activePage={activePage}
                  totalItemsCount={quantity}
                  onChange={handlePageChange.bind(this)}
                />
              )}
            </Col>
          </Row>
          <Row>
            <Col style={{ maxWidth: '8rem' }}>
              <ReactButton
                btnColor="confirmation"
                type="submit"
                size="sm"
                onClick={() => onModalToggle(false)}
                block
              >
                OK
              </ReactButton>
            </Col>
          </Row>
        </>
      </DefaultModal>
      <ChangeAccountCustomerPortfolio
        isCustomerPortfolioOpenModal={openModalConfirmation}
        toggleCustomerPortfolioModel={setOpenModalConfirmation}
        idResource={idResource}
        currentCustomerPortfolioId={customerPortfolioId}
        handleGetAccountsFromCustomerPortfolio={
          handleGetAccountsFromCustomerPortfolio
        }
        setSelectedRows={setSelectedRows}
      />
    </>
  );
};

export default HandleAccountsModal;
